import { useEffect, useState } from "react";
import {
  buildPublishNowRecordUpdates,
  buildScheduledPostUpdates,
  describeAxiosOrPublishError,
  getApiErrorMessage,
  normalizeScheduledTime,
  publishAndFetchPlatformPostId,
  publishSocialPost,
} from "../../../../services/socialTokenService";
import { saveChatPosts } from "../../../../services/contentService";
import { fetchPostByCustomerAndPostId, updatePostFields } from "../../../../services/postService";
import { fetchCustomerById } from "../../../../services/customerService";
import { useScheduleTimezone } from "../../../../hooks/useScheduleTimezone";
import { ConnectedProfile } from "../../../../types/customer";
import { DraftPost, Message, SocialPostData } from "../types";
import {
  buildPublishNowPayload,
  buildScheduledPayload,
  mergePostWithDraftForSocialPublish,
} from "../utils/postPayloadBuilders";
import { shouldDeferPublishToScheduler } from "../../../../utils/socialPostSchedule";
import { asConnectedProfileArray } from "../utils/connectedProfiles";
import { resolveDraftsLocalImageUrls } from "../utils/resolveCanvasDraftMedia";
import {
  draftPostToChatSaveRow,
  socialPostDataToChatSaveRow,
} from "../utils/draftToContentSaveRow";
import { CognitoAuthService } from "../../../../services/cognitoService";
import { useToast } from "../../../../context/ToastContext";
import { debug } from "../../../../utils/debugLogger";
import {
  instagramDraftHasPublishableMedia,
  instagramSocialPostDataHasPublishableMedia,
} from "../../../../utils/instagramPublishMediaValidation";
import {
  TIKTOK_CONSENT_REQUIRED_MESSAGE,
  isTikTokPlatform,
} from "../../../../features/tiktok/utils/tiktokOptions";
import { patchGeneratedPostPublishResult } from "../utils/generatedPostsPatchers";

type UsePostActionsParams = {
  customerId?: string;
  idToken: string;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  setStreamingGeneratedPosts: React.Dispatch<React.SetStateAction<import('../types').GeneratedPostsData | null>>;
  setPanelSocialPosts: React.Dispatch<React.SetStateAction<import('../types').GeneratedPostsData | null>>;
  setError: (value: string | null) => void;
};

const INSTAGRAM_IMAGE_REQUIRED_MESSAGE =
  "Add an image or video before publishing or scheduling on Instagram (Reels and video posts need a video).";

const isInstagramPost = (platform?: string) =>
  String(platform ?? "").toUpperCase().includes("INSTAGRAM");

export type ChatDraftWorkflowMode =
  | "publish_now"
  | "schedule_only"
  /** Save to library only — never calls live publish or platform scheduling APIs. */
  | "draft_only";

export type SingleCardBusy = {
  tempId: string;
  action: "save" | "publish" | "schedule";
};

type UsePostActionsResult = {
  isSavingPosts: boolean;
  /** Which bulk canvas action is running, if any (for button labels). */
  activeChatDraftMode: ChatDraftWorkflowMode | null;
  isPublishing: boolean;
  isScheduling: boolean;
  selectedPostData: SocialPostData | null;
  showScheduleModal: boolean;
  scheduleDateTime: string;
  /** IANA zone the modal's schedule picker should use (and the user can override). */
  scheduleTimezone: string;
  setShowScheduleModal: (value: boolean) => void;
  setSelectedPostData: (value: SocialPostData | null) => void;
  setScheduleDateTime: (value: string) => void;
  setScheduleTimezone: (tz: string) => void;
  /** "Save posts" chat action chip: save all to library as pending drafts only — never publishes. */
  handleSavePosts: (selectedPosts: DraftPost[]) => Promise<void>;
  /** Social canvas: save and publish now (clears per-post schedule; skips live publish if not connected). */
  handlePublishDraftsNow: (selectedPosts: DraftPost[]) => Promise<void>;
  /** Social canvas: save with each post’s scheduled time (requires time on every post). */
  handleScheduleDraftsOnly: (selectedPosts: DraftPost[]) => Promise<void>;
  /** Per-card: save draft only (no publish). Returns true when the post was saved successfully. */
  handleSaveSingleDraft: (post: DraftPost) => Promise<boolean>;
  handlePublishSingleDraft: (post: DraftPost) => Promise<boolean>;
  handleScheduleSingleDraft: (post: DraftPost) => Promise<boolean>;
  singleCardBusy: SingleCardBusy | null;
  handlePublish: (postData: SocialPostData) => Promise<void>;
  handleSchedule: (postData: SocialPostData) => void;
  handleScheduleSubmit: () => Promise<void>;
  hasConnectedAccounts: boolean;
  connectedProfiles: ConnectedProfile[];
};

export function usePostActions({
  customerId,
  idToken,
  setMessages,
  setStreamingGeneratedPosts,
  setPanelSocialPosts,
  setError,
}: UsePostActionsParams): UsePostActionsResult {
  const { showToast } = useToast();
  // Default scheduling clock for this customer. A draft that already carries its
  // own scheduled_timezone always wins over it.
  const { timezone: profileTimezone } = useScheduleTimezone(customerId);
  const toDateTimeLocalValue = (value?: string) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value.slice(0, 16);
    const tzOffset = d.getTimezoneOffset() * 60000;
    const local = new Date(d.getTime() - tzOffset);
    return local.toISOString().slice(0, 16);
  };

  const [isSavingPosts, setIsSavingPosts] = useState(false);
  const [activeChatDraftMode, setActiveChatDraftMode] =
    useState<ChatDraftWorkflowMode | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [selectedPostData, setSelectedPostData] = useState<SocialPostData | null>(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDateTime, setScheduleDateTime] = useState("");
  const [scheduleTimezone, setScheduleTimezone] = useState("");
  const [hasConnectedAccounts, setHasConnectedAccounts] = useState(false);
  const [connectedProfiles, setConnectedProfiles] = useState<ConnectedProfile[]>([]);
  const [singleCardBusy, setSingleCardBusy] = useState<SingleCardBusy | null>(null);

  useEffect(() => {
    if (!customerId || !idToken) return;
    const checkAccounts = async () => {
      try {
        const customer = await fetchCustomerById(customerId, idToken);
        const profiles = asConnectedProfileArray(customer?.connected_profiles);
        setConnectedProfiles(profiles);
        setHasConnectedAccounts(profiles.length > 0);
      } catch (err) {
        debug.error("Error checking connected accounts:", err);
        setConnectedProfiles([]);
        setHasConnectedAccounts(false);
      }
    };
    checkAccounts();
  }, [customerId, idToken]);

  const ensurePostForScheduling = async (
    postData: SocialPostData,
    scheduledTime: string,
    timezone?: string
  ): Promise<string | undefined> => {
    if (!customerId || !idToken) return undefined;

    const tz = timezone || postData.scheduled_timezone || profileTimezone;

    if (postData.post_id) {
      await updatePostFields(
        postData.post_id,
        buildScheduledPostUpdates(scheduledTime, tz),
        idToken,
        customerId
      );
      return postData.post_id;
    }

    const normalizedTime = normalizeScheduledTime(scheduledTime, tz);
    const result = await saveChatPosts(
      customerId,
      [socialPostDataToChatSaveRow(postData, normalizedTime, tz)],
      idToken
    );

    const createdPostId = result.post_ids?.[0];
    if (createdPostId) {
      await updatePostFields(
        createdPostId,
        buildScheduledPostUpdates(scheduledTime, tz),
        idToken,
        customerId
      );
    }
    return createdPostId;
  };

  const singleActionForMode = (
    mode: ChatDraftWorkflowMode
  ): SingleCardBusy["action"] => {
    if (mode === "draft_only") return "save";
    if (mode === "publish_now") return "publish";
    return "schedule";
  };

  const executeChatDraftWorkflow = async (
    selectedPosts: DraftPost[],
    mode: ChatDraftWorkflowMode,
    options?: { singleTempId?: string }
  ): Promise<boolean> => {
    if (!customerId || !idToken || selectedPosts.length === 0) {
      setError("Unable to save posts. Missing required data.");
      return false;
    }

    if (mode === "schedule_only") {
      const missingSchedule = selectedPosts.filter((p) => !p.scheduled_time?.trim());
      if (missingSchedule.length > 0) {
        setError(
          "Set a date and time on this post in the editor before scheduling, or use Save to keep a draft only."
        );
        return false;
      }
    }

    // TikTok (SGL-170) needs its settings panel (privacy, disclosure, consent) in
    // Post Manager; chat can only save it as a draft. Never publish/schedule from here.
    if (mode !== "draft_only" && selectedPosts.some((post) => isTikTokPlatform(post.platform))) {
      setError(TIKTOK_CONSENT_REQUIRED_MESSAGE);
      return false;
    }

    if (mode !== "draft_only") {
      const missingInstagramImage = selectedPosts.some(
        (post) => isInstagramPost(post.platform) && !instagramDraftHasPublishableMedia(post)
      );
      if (missingInstagramImage) {
        setError(INSTAGRAM_IMAGE_REQUIRED_MESSAGE);
        return false;
      }
    }

    let workingDrafts: DraftPost[] =
      mode === "publish_now"
        ? selectedPosts.map((p) => ({ ...p, scheduled_time: "" }))
        : selectedPosts;

    const singleId = options?.singleTempId;
    if (singleId) {
      setSingleCardBusy({ tempId: singleId, action: singleActionForMode(mode) });
    } else {
      setActiveChatDraftMode(mode);
      setIsSavingPosts(true);
    }
    setError(null);
    try {
      const userEmail = CognitoAuthService.getUserEmail(idToken);
      workingDrafts = await resolveDraftsLocalImageUrls(
        workingDrafts,
        customerId,
        userEmail,
        idToken
      );

      const postsToSave = workingDrafts.map((post) => {
        const row = draftPostToChatSaveRow(post);
        return {
          ...row,
          scheduled_time: post.scheduled_time
            ? normalizeScheduledTime(
                post.scheduled_time,
                post.scheduled_timezone || profileTimezone
              )
            : row.scheduled_time,
          scheduled_timezone: post.scheduled_timezone || profileTimezone,
        };
      });

      const result = await saveChatPosts(customerId, postsToSave, idToken);

      const profileList = asConnectedProfileArray(connectedProfiles);
      const isDraftPlatformConnected = (draft: DraftPost) => {
        const platformUpper = draft.platform.toUpperCase();
        const basePlatform = platformUpper.includes("REELS")
          ? platformUpper.split("_")[0]
          : platformUpper;
        return profileList.some(
          (p: ConnectedProfile) =>
            p.platform.toUpperCase() === basePlatform && p.details?.length > 0
        );
      };

      const scheduleWrites = result.post_ids.map((postId, index) => {
        const draft = workingDrafts[index];
        const scheduledTime = draft?.scheduled_time;
        if (!postId || !scheduledTime) return Promise.resolve();
        return updatePostFields(
          postId,
          buildScheduledPostUpdates(
            scheduledTime,
            draft?.scheduled_timezone || profileTimezone
          ),
          idToken,
          customerId
        );
      });
      await Promise.allSettled(scheduleWrites);

      if (mode === "draft_only") {
        const d = workingDrafts[0];
        const label = d?.platform ?? "Post";
        const msg =
          selectedPosts.length === 1
            ? `✅ **${label}** saved to your library as a draft. Nothing was published or sent to social channels.`
            : `✅ ${result.post_ids.length} posts saved to your library as drafts. Nothing was published.`;
        setMessages((prev) => [...prev, { role: "assistant", content: msg }]);
        showToast(msg, "success");
        return true;
      }

      const publishNowApprovalWrites = result.post_ids.map((postId, index) => {
        const draft = workingDrafts[index];
        if (!postId || draft?.scheduled_time || !isDraftPlatformConnected(draft)) {
          return Promise.resolve();
        }
        return updatePostFields(
          postId,
          buildPublishNowRecordUpdates(),
          idToken,
          customerId
        );
      });
      await Promise.allSettled(publishNowApprovalWrites);

      const publishActions = result.post_ids.map((postId, index) => {
        const draft = workingDrafts[index];
        if (!postId) return Promise.resolve({ skipped: true });

        if (!isDraftPlatformConnected(draft)) {
          debug.log(`⚠️ Skipping publish for ${draft.platform} (no account connected)`);
          return Promise.resolve({ skipped: true });
        }

        return (async () => {
          if (shouldDeferPublishToScheduler(draft.scheduled_time)) {
            return { deferredToScheduler: true as const };
          }

          const fetched = await fetchPostByCustomerAndPostId(customerId, postId, idToken);
          const post = fetched[0];
          if (!post?.post_id) {
            throw new Error(`Could not load saved post for ${draft.platform}.`);
          }

          const payload = mergePostWithDraftForSocialPublish(
            post,
            draft,
            draft.scheduled_time
              ? {
                  publishType: "scheduled",
                  scheduledTime: draft.scheduled_time,
                  timezone: draft.scheduled_timezone || profileTimezone,
                }
              : { publishType: "now" }
          );

          const isCarousel = payload.post_type === "CAROUSEL";
          const isMediaHeavyInstagramPost =
            String(payload.platform ?? "").toUpperCase().includes("INSTAGRAM") &&
            (isCarousel ||
              String(payload.post_type ?? "").toUpperCase() === "SINGLE_VIDEO" ||
              Boolean(payload.video_url));
          const isFacebookReelsPost =
            String(payload.platform ?? "").toUpperCase() === "FACEBOOK_REELS";
          const isLinkedInDocumentPost =
            String(payload.platform ?? "").toUpperCase() === "LINKEDIN" &&
            Boolean(payload.document_url || (payload.carousel_slides && payload.carousel_slides.length > 0));
          const isFacebookCarouselPost =
            String(payload.platform ?? "").toUpperCase() === "FACEBOOK" && isCarousel;
          const pubResult = await publishAndFetchPlatformPostId(payload, idToken, {
            maxRetries: isMediaHeavyInstagramPost
              ? 24
              : isFacebookReelsPost
                ? 20
                : isFacebookCarouselPost
                  ? 36
                  : isLinkedInDocumentPost
                    ? 12
                    : 8,
            delayMs: isMediaHeavyInstagramPost
              ? 5000
              : isFacebookReelsPost
                ? 4000
                : isFacebookCarouselPost
                  ? 5000
                  : isLinkedInDocumentPost
                    ? 5000
                    : 2500,
          });
          if (pubResult.error) {
            // Instagram carousels and videos often take > 30s to process asynchronously
            if (
              (isCarousel || payload.video_url) &&
              (pubResult.error === "platform_post_id not yet available" ||
                pubResult.error === "Instagram is still processing the video.")
            ) {
              return { ...pubResult, error: undefined, pending: true };
            }
            if (
              isFacebookReelsPost &&
              (pubResult.error === "platform_post_id not yet available" ||
                pubResult.error === "Facebook is still processing the Reel." ||
                pubResult.error === "Waiting for Facebook Reel to finish publishing.")
            ) {
              return { ...pubResult, error: undefined, pending: true };
            }
            if (
              isFacebookCarouselPost &&
              pubResult.error === "platform_post_id not yet available"
            ) {
              return { ...pubResult, error: undefined, pending: true };
            }
            throw new Error(pubResult.error);
          }
          return pubResult;
        })();
      });

      const publishResults = await Promise.allSettled(publishActions);
      const failedCount = publishResults.filter((r) => r.status === "rejected").length;
      const skippedCount = publishResults.filter(
        (r) => r.status === "fulfilled" && (r.value as { skipped?: boolean })?.skipped
      ).length;
      const deferredCount = publishResults.filter(
        (r) =>
          r.status === "fulfilled" &&
          (r.value as { deferredToScheduler?: boolean })?.deferredToScheduler
      ).length;
      const publishedNowCount = publishResults.filter((r) => {
        const v = r.status === "fulfilled" ? (r.value as Record<string, unknown>) : null;
        return (
          r.status === "fulfilled" &&
          !v?.skipped &&
          !v?.deferredToScheduler
        );
      }).length;

      // Patch platform_post_id / business_profile_id / platform_response back onto
      // each successfully-published DraftPost so the live post link appears on the card.
      publishResults.forEach((r, index) => {
        if (r.status !== "fulfilled") return;
        const v = r.value as Record<string, unknown>;
        if (!v?.platform_post_id) return;
        const tempId = workingDrafts[index]?.temp_id;
        if (!tempId) return;
        const publishData = {
          platform_post_id: v.platform_post_id as string,
          business_profile_id: (v.business_page_id ?? v.business_profile_id ?? "") as string,
          platform_response: v.platform_response,
        };
        const patchFn = (prev: import('../types').GeneratedPostsData | null) =>
          patchGeneratedPostPublishResult(prev, tempId, publishData);
        setStreamingGeneratedPosts((prev) => patchFn(prev));
        setPanelSocialPosts((prev) => patchFn(prev));
        setMessages((prev) =>
          prev.map((m) => {
            if (!m.generatedPosts?.posts?.some((p) => p.temp_id === tempId)) return m;
            const next = patchGeneratedPostPublishResult(m.generatedPosts, tempId, publishData);
            return next ? { ...m, generatedPosts: next } : m;
          })
        );
      });

      const publishErrors: string[] = [];
      publishResults.forEach((r, index) => {
        if (r.status !== "rejected") return;
        const draft = workingDrafts[index];
        publishErrors.push(
          `${draft?.platform ?? "Post"}: ${describeAxiosOrPublishError(r.reason, "Unknown error")}`
        );
      });

      const isSingle = Boolean(singleId && selectedPosts.length === 1);
      const platLabel = workingDrafts[0]?.platform ?? "Post";

      let responseContent = `✅ ${result.post_ids.length} ${
        result.post_ids.length === 1 ? "post" : "posts"
      } saved to library successfully!`;

      if (mode === "publish_now") {
        const pendingCount = publishResults.filter(
          (r) => r.status === "fulfilled" && (r.value as Record<string, unknown>)?.pending
        ).length;
        const completeCount = publishedNowCount - pendingCount;

        if (isSingle) {
          responseContent =
            publishedNowCount > 0
              ? pendingCount > 0 
                ? `✅ **${platLabel}** is processing in the background (media container publishing may take roughly 1 minute).`
                : `✅ **${platLabel}** published now.`
              : skippedCount > 0
                ? `✅ **${platLabel}** saved as a draft (connect this platform to publish).`
                : `✅ **${platLabel}** saved. ${failedCount > 0 ? "See errors below." : ""}`;
        } else {
          responseContent =
            `✅ ${result.post_ids.length} ${result.post_ids.length === 1 ? "post" : "posts"} saved. ` +
            (publishedNowCount > 0
              ? (pendingCount > 0 
                  ? `${completeCount} published now, ${pendingCount} processing in the background (may take ~1 min).`
                  : `${publishedNowCount} published now.`)
              : "No live publishes (connect accounts or check errors below).");
          if (skippedCount > 0) {
            responseContent += ` ${skippedCount} saved as drafts only (account not connected).`;
          }
        }
      } else if (mode === "schedule_only") {
        if (isSingle) {
          responseContent =
            deferredCount > 0
              ? `✅ **${platLabel}** saved for its scheduled time (scheduler will send it).`
              : publishedNowCount > 0
                ? `✅ **${platLabel}** scheduled with the platform.`
                : skippedCount > 0
                  ? `✅ **${platLabel}** saved to the library only — connect the account to schedule on the platform.`
                  : `✅ **${platLabel}** saved with your schedule.`;
        } else {
          responseContent =
            `✅ ${result.post_ids.length} ${result.post_ids.length === 1 ? "post" : "posts"} saved with your schedule.`;
          if (deferredCount > 0) {
            responseContent += ` ${deferredCount} queued for the scheduler at their scheduled times.`;
          }
          if (publishedNowCount > 0) {
            responseContent += ` ${publishedNowCount} scheduled with the platform.`;
          }
          if (skippedCount > 0) {
            responseContent += ` ${skippedCount} saved to the library only (connect the account to send on schedule).`;
          }
        }
      } else {
        if (publishedNowCount > 0) {
          const pendingCount = publishResults.filter(
            (r) => r.status === "fulfilled" && (r.value as Record<string, unknown>)?.pending
          ).length;
          const completeCount = publishedNowCount - pendingCount;
          
          if (completeCount > 0) {
            responseContent += ` ${completeCount} post(s) published to the platform instantly.`;
          }
          if (pendingCount > 0) {
            responseContent += ` ${pendingCount} post(s) are processing in the background (media container publishing may take roughly 1 minute).`;
          }
        }
        if (deferredCount > 0) {
          responseContent += ` ${deferredCount} post(s) saved for their scheduled time (queued for the scheduler — not published immediately).`;
        }
        if (skippedCount > 0) {
          responseContent += ` ${skippedCount} post(s) were saved as drafts only (account not connected).`;
        }
      }

      if (failedCount > 0) {
        responseContent += ` ${failedCount} post(s) encountered errors during scheduling/publishing.`;
        if (publishErrors.length > 0) {
          responseContent += `\n\n${publishErrors.map((e) => `• ${e}`).join("\n")}`;
        }
      }

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: responseContent,
        },
      ]);
      if (publishedNowCount > 0 || deferredCount > 0) {
        showToast(responseContent, "success");
      } else {
        showToast(responseContent, "info");
      }
      return true;
    } catch (err) {
      debug.error("Error saving posts:", err);
      setError(err instanceof Error ? err.message : "Failed to save posts. Please try again.");
      return false;
    } finally {
      if (singleId) {
        setSingleCardBusy(null);
      } else {
        setIsSavingPosts(false);
        setActiveChatDraftMode(null);
      }
    }
  };

  const handleSavePosts = async (selectedPosts: DraftPost[]) => {
    await executeChatDraftWorkflow(selectedPosts, "draft_only");
  };

  const handlePublishDraftsNow = async (selectedPosts: DraftPost[]) => {
    await executeChatDraftWorkflow(selectedPosts, "publish_now");
  };

  const handleScheduleDraftsOnly = async (selectedPosts: DraftPost[]) => {
    await executeChatDraftWorkflow(selectedPosts, "schedule_only");
  };

  const handleSaveSingleDraft = (post: DraftPost) =>
    executeChatDraftWorkflow([post], "draft_only", { singleTempId: post.temp_id });

  const handlePublishSingleDraft = (post: DraftPost) =>
    executeChatDraftWorkflow([post], "publish_now", { singleTempId: post.temp_id });

  const handleScheduleSingleDraft = (post: DraftPost) =>
    executeChatDraftWorkflow([post], "schedule_only", { singleTempId: post.temp_id });

  const handlePublish = async (postData: SocialPostData) => {
    if (!customerId || !idToken) {
      setError("Missing customer ID or authentication token");
      return;
    }

    if (postData.platforms?.some((platform) => isTikTokPlatform(platform))) {
      setError(TIKTOK_CONSENT_REQUIRED_MESSAGE);
      return;
    }

    const isInstagram = postData.platforms?.some((platform) => isInstagramPost(platform));
    if (isInstagram && !instagramSocialPostDataHasPublishableMedia(postData)) {
      setError(INSTAGRAM_IMAGE_REQUIRED_MESSAGE);
      return;
    }

    setIsPublishing(true);
    setError(null);
    try {
      await publishSocialPost(buildPublishNowPayload(customerId, postData), idToken);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Your social post has been published successfully! 🎉",
        },
      ]);
      showToast("Published successfully! 🎉", "success");
    } catch (err) {
      debug.error("Error publishing post:", err);
      setError(getApiErrorMessage(err, "Failed to publish post. Please try again."));
    } finally {
      setIsPublishing(false);
    }
  };

  const handleSchedule = (postData: SocialPostData) => {
    if (postData.platforms?.some((platform) => isTikTokPlatform(platform))) {
      setError(TIKTOK_CONSENT_REQUIRED_MESSAGE);
      return;
    }
    setSelectedPostData(postData);
    // Keep as UTC ISO — the modal's ScheduleDateTimeField renders it in
    // scheduleTimezone, so the user always sees the customer's clock.
    // Converting to browser-local here would make handleScheduleSubmit
    // reinterpret a Manila wall-clock as Chicago, giving the wrong instant.
    setScheduleDateTime(postData.scheduled_time || "");
    setScheduleTimezone(postData.scheduled_timezone || profileTimezone);
    setShowScheduleModal(true);
  };

  const handleScheduleSubmit = async () => {
    if (!selectedPostData || !customerId || !idToken || !scheduleDateTime) {
      setError("Please select a date and time for scheduling");
      return;
    }

    const isInstagram = selectedPostData.platforms?.some((platform: string) =>
      isInstagramPost(platform)
    );
    if (isInstagram && !instagramSocialPostDataHasPublishableMedia(selectedPostData)) {
      setError(INSTAGRAM_IMAGE_REQUIRED_MESSAGE);
      return;
    }

    setIsScheduling(true);
    setError(null);
    try {
      const tz = scheduleTimezone || selectedPostData.scheduled_timezone || profileTimezone;
      const postId = await ensurePostForScheduling(selectedPostData, scheduleDateTime, tz);
      const normalizedTime = normalizeScheduledTime(scheduleDateTime, tz);
      const deferToScheduler = shouldDeferPublishToScheduler(normalizedTime);
      if (!deferToScheduler) {
        const scheduledPayload = buildScheduledPayload(
          customerId,
          { ...selectedPostData, post_id: postId || selectedPostData.post_id },
          scheduleDateTime,
          tz
        );
        await publishSocialPost(scheduledPayload, idToken);
      }
      const platformLabel = selectedPostData.platforms?.[0] || "facebook";
      const when = new Date(normalizedTime).toLocaleString();
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: deferToScheduler
            ? `Saved for ${when} (${platformLabel}). It will go out at that time via the scheduler — nothing was published immediately.`
            : `Scheduled for ${when} (${platformLabel}).`,
        },
      ]);
      showToast(deferToScheduler ? "Scheduled successfully (Local Scheduler)" : "Scheduled successfully!", "success");
      setShowScheduleModal(false);
      setSelectedPostData(null);
      setScheduleDateTime("");
    } catch (err) {
      debug.error("Error scheduling post:", err);
      setError(getApiErrorMessage(err, "Failed to schedule post. Please try again."));
    } finally {
      setIsScheduling(false);
    }
  };

  return {
    isSavingPosts,
    activeChatDraftMode,
    isPublishing,
    isScheduling,
    selectedPostData,
    showScheduleModal,
    scheduleDateTime,
    scheduleTimezone,
    setShowScheduleModal,
    setSelectedPostData,
    setScheduleDateTime,
    setScheduleTimezone,
    handleSavePosts,
    handlePublishDraftsNow,
    handleScheduleDraftsOnly,
    handleSaveSingleDraft,
    handlePublishSingleDraft,
    handleScheduleSingleDraft,
    singleCardBusy,
    handlePublish,
    handleSchedule,
    handleScheduleSubmit,
    hasConnectedAccounts,
    connectedProfiles,
  };
}
